<template>
	<div
		class="pt-8 px-12 pb-12 rounded-lg border border-gray-500 shadow bg-white dark:bg-gray-600"
		:class="{'border border-gray-100': isDraggedOver}"
		@dragover.prevent="isDraggedOver = true"
		@drop.prevent="onDropFiles"
	>
		<RichTextEditor
			:id="`comment-${comment.id}`"
			v-model="comment.content"
			data-test="comment-rich-text-editor"
			placeholder="Post a comment"
			editable
			enableTimestamps
			@keydown="onKeyDown"
			@close="close"
		/>
		<div v-if="attachments.length" class="h-auto space-y-4 mt-12">
			<FileAttachment
				v-for="attachment in attachments"
				:key="attachment.id"
				:attachment="attachment"
				editing
				@remove="removeAttachment(attachment)"
			/>
		</div>
		<div class="flex items-center mt-16">
			<AttachFileButton :isDisabled="loading" @attach="onAttachFiles" />
			<button class="ml-auto mr-12 px-8 h-28 link-gray" @click="close">Cancel</button>
			<BaseButton
				data-test="post-comment-button"
				:loading="loading"
				class="button-blue text-strong-base"
				:disabled="submitButtonDisabled"
				@click="onComment"
			>
				Comment
			</BaseButton>
		</div>
	</div>
</template>

<script>
import {defineAsyncComponent} from 'vue'
import upsertCommentAndSessionNotificationSubscription from 'shared/apollo/upsertCommentAndSessionNotificationSubscription'
import AttachFileButton from 'shared/components/AttachFileButton.vue'
import FileAttachment from 'shared/components/FileAttachment.vue'
import manageAttachments from 'shared/mixins/manageAttachments'
import delay from 'shared/utils/delay'

export default {
	name: 'SessionCommentForm',
	components: {
		AttachFileButton,
		FileAttachment,
		RichTextEditor: defineAsyncComponent(() => import('shared/components/RichTextEditor.vue')),
	},
	mixins: [manageAttachments],
	props: {
		originalComment: {
			type: Object,
			default: () => ({}),
		},
		commentAttachments: {
			type: Array,
			default: () => [],
		},
	},
	emits: ['close'],
	data() {
		return {
			comment: {
				id: this.originalComment.id || undefined,
				content: this.originalComment.content || '',
				sessionId: this.$route.params.sessionId,
			},
			loading: false,
			isDraggedOver: false,
		}
	},
	computed: {
		submitButtonDisabled() {
			if (!this.comment.content && !this.attachments.length) return true
			if (this.hasFailedAttachmentFileUploads) return true
			return null
		},
		hasUnsavedChanges() {
			if ((this.origialComment?.content?.trim() || '') !== this.comment.content?.trim()) return true
			if (this.newAttachments.length) return true
			if (this.removedAttachments.length) return true
			return false
		},
	},
	watch: {
		commentAttachments: {
			immediate: true,
			handler(attachments) {
				if (!Array.isArray(attachments)) return
				this.currentAttachments = attachments
			},
		},
		hasUnsavedChanges(hasUnsavedChanges) {
			if (!sessionStorage) return
			if (hasUnsavedChanges) {
				sessionStorage.hasUnsavedChanges = true
			} else {
				delete sessionStorage.hasUnsavedChanges
			}
		},
	},
	methods: {
		onKeyDown(event) {
			if ((event.ctrlKey || event.metaKey) && event.keyCode === 13) {
				this.onComment()
				return
			}
		},
		close() {
			this.$emit('close')
			if (sessionStorage) delete sessionStorage.hasUnsavedChanges
		},
		onDropFiles(event) {
			this.isDraggedOver = false
			this.uploadAttachmentFiles(event.dataTransfer.files)
		},
		onAttachFiles(event) {
			this.uploadAttachmentFiles(event.target.files)
		},
		onComment() {
			if (!this.comment.content.length && !this.newAttachments.length && !this.removedAttachments.length) {
				return this.close()
			}
			this.save()
		},
		async save() {
			this.loading = true
			try {
				// await attachment uploads (if existent) to finish before saving the comment,
				// to prevent the session already showing the new comment without the attachment.
				while (this.isUploadingAttachmentFiles) await delay(200)

				const result = await upsertCommentAndSessionNotificationSubscription({
					comment: this.comment,
					userId: this.$auth.userId.value,
				})

				const commentId = result.data.insert_comments_one.id
				await this.saveAttachmentChanges({sessionId: this.$route.params.sessionId, commentId})

				this.close()
			} finally {
				this.loading = false
			}
		},
	},
}
</script>
